import { useEffect, useState, useCallback } from "react";
import { format } from "date-fns";
import * as XLSX from "xlsx";

import { apiLabs, apiIDW } from "../../Services/api";
import { store } from "../../store";
import history from "../../Services/history";
import Denied from "../../Components/Denied";

import {
  globalCSS,
  page,
  dash,
  header,
  filterBar,
  distribution,
  brmap,
  footer,
  colors,
  deliveryColors,
  deliveryRow,
  donut,
  performance as perfStyle,
  carriers as carriersStyle,
  modal as modalStyle,
} from "./style";
import { BR_PATHS, BR_CENTROIDS } from "./brazilGeo";

// ─────────────────────────────────────────────────────────────────────────────
// Constantes da requisição
// ─────────────────────────────────────────────────────────────────────────────
const SALES_CHANNELS = [
  { IDSalesChannel: 0, SalesChannelName: "Vtex" },
  { IDSalesChannel: 4, SalesChannelName: "idworks" },
];
const REFRESH_MS = 5 * 60 * 1000;

// Janela de consulta por data estimada de entrega: 30 dias com HOJE no meio.
// Para trás entram as promessas já vencidas (o que atrasou continua à vista);
// para frente, as que ainda vão vencer — é o que dá enxergar a carga que vem.
const WINDOW_DAYS_BACK = 14;
const WINDOW_DAYS_AHEAD = 15;

// ─────────────────────────────────────────────────────────────────────────────
// Timeline de 6 estágios do painel do topo. Ordem cronológica do ciclo:
//   não entregues (em rota → risco → atrasado) → entregues (antecipado → no
//   prazo → com atraso). Toda a classificação é pela visão do CLIENTE, exceto
//   "EM RISCO" que olha o SLA da transportadora (cliente OK, carrier atrasou).
// ─────────────────────────────────────────────────────────────────────────────
const GROUP_KEYS = [
  "andamento",
  "risco",
  "atrasados",
  "antecipado",
  "noPrazo",
  "entregueAtraso",
];

const GROUPS = {
  andamento: {
    label: "EM ANDAMENTO",
    helper: "em rota, dentro do prazo",
    color: "#2A75B0", // azul
  },
  risco: {
    label: "EM RISCO",
    helper: "não entregue, transportadora atrasou",
    color: "#E08A1E", // laranja
  },
  atrasados: {
    label: "ATRASADOS",
    helper: "não entregue, prazo estourado",
    color: "#D04638", // vermelho
  },
  antecipado: {
    label: "ANTECIPADOS",
    helper: "entregue antes do prazo",
    color: "#1F8A4F", // verde vivo
  },
  noPrazo: {
    label: "ENTREGUE NO PRAZO",
    helper: "entregue dentro do prazo",
    color: "#2C8D5E", // verde
  },
  entregueAtraso: {
    label: "ENTREGUE C/ ATRASO",
    helper: "entregue após o prazo",
    color: "#9E3B2F", // vinho
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers de data
// ─────────────────────────────────────────────────────────────────────────────
function getLocalDateStr(date) {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().split("T")[0];
}

function getDateStrDaysAgo(daysAgo) {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return getLocalDateStr(d);
}

function getDateStrDaysAhead(daysAhead) {
  const d = new Date();
  d.setDate(d.getDate() + daysAhead);
  return getLocalDateStr(d);
}

// ─────────────────────────────────────────────────────────────────────────────
// Filtro de período (data + hora)
// ─────────────────────────────────────────────────────────────────────────────
// Os inputs são datetime-local ("2026-11-03T08:00"). A hora entra com 00:00 no
// início e 23:59 no fim, então escolher só as datas se comporta como um filtro
// de dia inteiro — a hora só estreita se o usuário mexer nela de propósito.
//
// Isso importa porque ShippingEstimateDate é uma promessa de DATA e costuma
// vir às 00:00. Se o início assumisse a hora atual, escolher "hoje" derrubaria
// o dia inteiro.
const DEFAULT_START_TIME = "00:00";
const DEFAULT_END_TIME = "23:59";

const toLocalInput = (dateStr, time) => `${dateStr}T${time}`;

/** "2026-11-03T08:00" → "2026-11-03", que é o formato que a API aceita. */
const datePartOf = (localInput) => String(localInput || "").slice(0, 10);

/** "2026-11-03T08:00" → timestamp local, sem conversão de fuso. */
function localInputToMs(localInput) {
  const m = String(localInput || "").match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return null;
  return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]).getTime();
}

/** Janela padrão do dashboard: 30 dias com hoje no meio. */
function defaultRange() {
  return {
    from: toLocalInput(getDateStrDaysAgo(WINDOW_DAYS_BACK), DEFAULT_START_TIME),
    to: toLocalInput(getDateStrDaysAhead(WINDOW_DAYS_AHEAD), DEFAULT_END_TIME),
  };
}

/** Recorte fino por data+hora sobre o que a API devolveu em dias inteiros. */
function filterByEstimateRange(orders, range) {
  const fromMs = localInputToMs(range.from);
  const toMs = localInputToMs(range.to);
  if (fromMs == null && toMs == null) return orders;
  return orders.filter((order) => {
    const d = parseISO(order.ShippingEstimateDate);
    // Sem data estimada não dá pra dizer se cai no período — fica de fora,
    // senão o filtro mostraria pedido que o usuário não pediu.
    if (!d) return false;
    const ms = d.getTime();
    if (fromMs != null && ms < fromMs) return false;
    if (toMs != null && ms > toMs) return false;
    return true;
  });
}

// Considera um pedido como "entregue hoje" se DeliveredDate cair no dia local atual
function isDeliveredToday(order, todayStr) {
  const d = parseISO(order.DeliveredDate);
  if (!d) return false;
  return getLocalDateStr(d) === todayStr;
}

// ─────────────────────────────────────────────────────────────────────────────
// Paginação genérica do apiIDW. Esquema de paginação (param `Page`, P maiúsculo):
//   • 1ª requisição → Page=0
//   • 2ª requisição → Page=1
//   • 3ª requisição → Page=2  … e assim por diante
// IMPORTANTE: a API deixou de devolver um tamanho fixo por página (agora varia,
// ~2500 a 3000). Portanto NÃO dá mais pra usar "página menor que o máximo = fim".
// Critério de parada:
//   • página vazia                              → fim
//   • página sem NENHUM pedido novo por IDOrder → backend repetiu, encerra
//   • cap de páginas (MAX_PAGES)                → salvaguarda final
// Sempre retorna deduplicado por IDOrder, evitando contagem inflada.
// ─────────────────────────────────────────────────────────────────────────────
// Dobrou junto com a janela (15 → 30 dias). Com ~2500–3000 por página, cobre
// bem mais do que o volume real — é salvaguarda contra laço infinito, não teto
// de negócio. Se o aviso abaixo aparecer, é aqui que se mexe.
const MAX_PAGES = 50;

async function fetchAllPages(endpoint, params) {
  const byId = new Map();
  let completo = false;
  // Page=0 → 1ª página; Page=1 → 2ª; Page=2 → 3ª …
  for (let pageNum = 0; pageNum < MAX_PAGES; pageNum++) {
    const response = await apiIDW.get(endpoint, {
      params: { ...params, Page: pageNum },
    });
    const data = Array.isArray(response.data) ? response.data : [];
    if (data.length === 0) {
      completo = true;
      break;
    }

    let novos = 0;
    for (const o of data) {
      const key = o?.IDOrder != null ? o.IDOrder : `${o?.Order}#${byId.size}`;
      if (!byId.has(key)) {
        byId.set(key, o);
        novos += 1;
      }
    }

    // Sem NENHUM item novo (backend repetiu a mesma página, não respeitou `Page`) → para
    if (novos === 0) {
      completo = true;
      break;
    }
    // A API passou a variar o tamanho da página (~2500–3000), então NÃO usamos o
    // tamanho pra detectar o fim. Enquanto vier registro novo, busca a próxima;
    // encerra só quando vier VAZIA (acima) ou sem nada novo (regra acima).
  }

  // Sair pelo cap significa que a consulta foi truncada: a tela mostra menos
  // pedidos do que existem e ninguém percebe. Melhor gritar no console.
  if (!completo) {
    console.warn(
      `[Tracking] ${endpoint} atingiu o teto de ${MAX_PAGES} páginas ` +
        `(${byId.size} pedidos) — o resultado está truncado. Aumente MAX_PAGES.`,
      params
    );
  }

  return [...byId.values()];
}

// ─────────────────────────────────────────────────────────────────────────────
// Construção das métricas a partir do array de trackings devolvido pelo apiIDW.
// Campos relevantes do payload:
//   StatusDeliveryConsumer  → "Dentro do prazo" | "Entregue no dia" | "Atrasado" | "Entregue com atraso" | ...
//   StatusDeliveryCarrier   → idem
//   StatusTracking          → "Em andamento" | "Finalizado"
//   DeliveredDate / FinishDate → ISO ou null (null = ainda em rota)
//   ShippingDate            → ISO da expedição
//   ShippingEstimateDate    → ISO da promessa ao cliente
//   ShippingState           → UF (ex.: "SP")
//   ShippingCity            → cidade
//   ValueShipping           → string "14.99"
//   SupplierNameCorporateName → nome da transportadora
//
// CarrierDeliveryDateDifference e ConsumerDeliveryDateDifference são ignorados
// quando assumem o valor sentinela ~20593 (resultado de CarrierDeliveryDate ==
// 1970-01-01). Calculamos as diferenças via datas reais.
// ─────────────────────────────────────────────────────────────────────────────
const norm = (s) => String(s ?? "").trim().toUpperCase();

// Remove acentos para comparações robustas de nome de transportadora
const stripAccents = (s) =>
  String(s ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "");

// Transportadoras que NÃO entram em nenhuma contabilização do dashboard
function isExcludedCarrier(name) {
  const c = stripAccents(name).trim().toUpperCase();
  return c.includes("RETIRADA EM MAOS") || c.includes("TRANSPORTADORA A COBRAR");
}

// Status de pedido que NÃO entram em nenhuma contabilização do dashboard
// (Extraviado, Devolvido, Em devolução, Cancelado).
function isExcludedStatus(status) {
  const s = stripAccents(status).trim().toUpperCase();
  return (
    s.includes("EXTRAVIAD") ||
    s.includes("DEVOL") || // Devolvido e Em devolução
    s.includes("CANCELAD")
  );
}

const EPOCH_MS = 86400000; // 1 dia em ms · usado também como cutoff de epoch

function parseISO(s) {
  if (!s) return null;
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  if (d.getTime() < EPOCH_MS * 365) return null; // descarta 1970-01-01 e vizinhos
  return d;
}

function isLate(statusStr) {
  const s = norm(statusStr);
  return s.includes("ATRAS"); // "Atrasado" ou "Entregue com atraso"
}

function isAntecipated(statusStr) {
  return norm(statusStr).includes("ANTECIP");
}

// Classificação da timeline (6 estágios). Visão do cliente, com exceção do
// "risco" que olha o SLA da transportadora.
//   Não entregue:  atrasados (cliente atrasou) · risco (carrier atrasou) · andamento
//   Entregue:      entregueAtraso (cliente atrasou) · antecipado · noPrazo
function classifyTimeline(order) {
  const isClosed =
    !!parseISO(order.DeliveredDate) ||
    norm(order.StatusTracking) === "FINALIZADO";
  const consumerStatus = order.StatusDeliveryConsumer;

  if (isClosed) {
    if (isLate(consumerStatus)) return "entregueAtraso";
    if (isAntecipated(consumerStatus)) return "antecipado";
    return "noPrazo";
  }

  if (isLate(consumerStatus)) return "atrasados";
  if (isLate(order.StatusDeliveryCarrier)) return "risco";
  return "andamento";
}

// Classificação operacional usada nos novos painéis. Considera APENAS o SLA do
// cliente (StatusDeliveryConsumer) — o atraso da transportadora não conta aqui.
//   transito   → aberto, cliente sem atraso
//   ocorrencia → aberto, cliente já atrasou
//   sucesso    → concluído, cliente sem atraso
//   problema   → concluído, cliente atrasou
function classifyDelivery(order) {
  const delivered = !!parseISO(order.DeliveredDate);
  const finalized = norm(order.StatusTracking) === "FINALIZADO";
  const isClosed = delivered || finalized;
  const consumerLate = isLate(order.StatusDeliveryConsumer);

  if (isClosed) return consumerLate ? "problema" : "sucesso";
  return consumerLate ? "ocorrencia" : "transito";
}

function buildStats(allOrders) {
  // Remove pedidos de transportadoras que não devem ser contabilizadas
  // e pedidos com StatusOrder Extraviado / Devolvido / Cancelado.
  const orders = allOrders.filter(
    (o) =>
      !isExcludedCarrier(o.SupplierNameCorporateName) &&
      !isExcludedStatus(o.StatusOrder)
  );

  const counts = {
    andamento: 0,
    risco: 0,
    atrasados: 0,
    antecipado: 0,
    noPrazo: 0,
    entregueAtraso: 0,
  };
  // Contagem da JANELA COMPLETA (30 dias) — sem o filtro de "entregue hoje".
  const countsFull = {
    andamento: 0,
    risco: 0,
    atrasados: 0,
    antecipado: 0,
    noPrazo: 0,
    entregueAtraso: 0,
  };
  const deliveryCounts = { transito: 0, ocorrencia: 0, sucesso: 0, problema: 0 };
  const timelineOrders = {
    andamento: [],
    risco: [],
    atrasados: [],
    antecipado: [],
    noPrazo: [],
    entregueAtraso: [],
  };
  const timelineOrdersFull = {
    andamento: [],
    risco: [],
    atrasados: [],
    antecipado: [],
    noPrazo: [],
    entregueAtraso: [],
  };
  const deliveriesByUF = new Map();
  const lateByUF = new Map();
  const carrierStats = new Map(); // carrier → { name, total, sucesso, transito, ocorrencia, problema }

  // Filtro de "entregues hoje" (DeliveredDate no dia local atual). Aplica-se:
  //   • timeline → 3 quadrantes de entregues (antecipado/noPrazo/entregueAtraso)
  //   • donut    → lado CONCLUÍDAS (sucesso/problema); EM ABERTO mantém janela total
  //   • performance → derivada do donut filtrado
  const todayStr = getLocalDateStr(new Date());
  const CLOSED_TL = new Set(["antecipado", "noPrazo", "entregueAtraso"]);
  const CLOSED_DONUT = new Set(["sucesso", "problema"]);

  for (const o of orders) {
    const tl = classifyTimeline(o);
    const delivBucket = classifyDelivery(o);
    const todayDeliv = isDeliveredToday(o, todayStr);

    // Janela completa (30 dias): conta TODOS, sem o filtro de "entregue hoje"
    countsFull[tl] += 1;
    timelineOrdersFull[tl].push(o);

    // Timeline: quadrantes de entregue contam só HOJE
    if (!CLOSED_TL.has(tl) || todayDeliv) {
      counts[tl] += 1;
      timelineOrders[tl].push(o);
    }

    // Donut: lado CONCLUÍDAS conta só HOJE; lado EM ABERTO conta janela inteira
    if (!CLOSED_DONUT.has(delivBucket) || todayDeliv) {
      deliveryCounts[delivBucket] += 1;
    }

    const uf = norm(o.ShippingState);
    const carrier = o.SupplierNameCorporateName || "—";

    // Painel "ATRASOS · POR UF" considera apenas o SLA do CLIENTE
    const consumerLate = isLate(o.StatusDeliveryConsumer);

    if (uf) {
      let entry = deliveriesByUF.get(uf);
      if (!entry) {
        entry = { uf, count: 0, orders: [] };
        deliveriesByUF.set(uf, entry);
      }
      entry.count += 1;
      entry.orders.push(o);

      if (consumerLate) {
        let lateEntry = lateByUF.get(uf);
        if (!lateEntry) {
          lateEntry = { uf, count: 0, total: 0, orders: [] };
          lateByUF.set(uf, lateEntry);
        }
        lateEntry.count += 1;
        lateEntry.orders.push(o);
      }
    }

    if (carrier && carrier !== "—") {
      const cs =
        carrierStats.get(carrier) ||
        { name: carrier, total: 0, sucesso: 0, transito: 0, ocorrencia: 0, problema: 0 };
      cs.total += 1;
      cs[delivBucket] += 1;
      carrierStats.set(carrier, cs);
    }
  }

  const total = orders.length;

  for (const [uf, entry] of lateByUF.entries()) {
    const totalUf = deliveriesByUF.get(uf)?.count || 0;
    entry.total = totalUf;
    entry.pct = totalUf > 0 ? (entry.count / totalUf) * 100 : 0;
  }

  const deliveriesList = [...deliveriesByUF.values()].sort(
    (a, b) => b.count - a.count
  );
  const lateList = [...lateByUF.values()].sort((a, b) => b.count - a.count);

  // Carriers ordenados por total (para a barra empilhada do painel)
  const carriersList = [...carrierStats.values()].sort(
    (a, b) => b.total - a.total
  );

  // Performance de entrega: % no prazo sobre o total de pedidos concluídos
  const closedCount = deliveryCounts.sucesso + deliveryCounts.problema;
  const perfRate =
    closedCount > 0 ? (deliveryCounts.sucesso / closedCount) * 100 : null;

  return {
    total,
    counts,
    countsFull,
    timelineOrders,
    timelineOrdersFull,
    deliveriesList,
    lateList,
    delivery: {
      total,
      open: {
        transito: deliveryCounts.transito,
        ocorrencia: deliveryCounts.ocorrencia,
      },
      closed: {
        sucesso: deliveryCounts.sucesso,
        problema: deliveryCounts.problema,
      },
    },
    performance: {
      pct: perfRate,
      onTime: deliveryCounts.sucesso,
      late: deliveryCounts.problema,
    },
    carriersList,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Formatadores
// ─────────────────────────────────────────────────────────────────────────────
const fmtInt = (v) =>
  v == null || isNaN(v) ? "—" : Math.round(Number(v)).toLocaleString("pt-BR");
const fmtPct = (v) =>
  v == null || isNaN(v)
    ? "—"
    : `${Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}`;

// ─────────────────────────────────────────────────────────────────────────────
// Componentes auxiliares
// ─────────────────────────────────────────────────────────────────────────────
function Clock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    let timer;
    function scheduleNext() {
      const delay = 1000 - (Date.now() % 1000);
      timer = setTimeout(() => {
        setTime(new Date());
        scheduleNext();
      }, delay);
    }
    scheduleNext();
    return () => clearTimeout(timer);
  }, []);
  return <div style={header.clock}>{time.toLocaleTimeString("pt-BR")}</div>;
}

function DistributionPanel({ title, total, counts, timelineOrders, onSelect }) {
  const totalNum = total || 0;

  return (
    <section style={distribution.wrap}>
      <div style={distribution.head}>
        <h2 style={distribution.title}>{title}</h2>
        <div style={distribution.totalBlock}>
          <span style={distribution.totalLabel}>PEDIDOS ANALISADOS</span>
          <span style={distribution.totalValue}>{fmtInt(totalNum)}</span>
        </div>
      </div>

      <div style={distribution.grid}>
        {GROUP_KEYS.map((key) => {
          const g = GROUPS[key];
          const v = counts[key] || 0;
          const pct = totalNum > 0 ? (v / totalNum) * 100 : 0;
          const orders = timelineOrders?.[key] || [];
          const clickable = orders.length > 0;
          return (
            <div
              key={key}
              className={clickable ? "tracking-stage" : undefined}
              style={distribution.stage(g.color, clickable)}
              onClick={
                clickable
                  ? () => onSelect({ title: g.label, orders })
                  : undefined
              }
              title={clickable ? `Ver ${v} pedidos · ${g.label}` : undefined}
            >
              <div style={distribution.stageLabel(g.color)}>{g.label}</div>
              <div style={distribution.stageValue}>
                {fmtInt(v)}
                <span style={distribution.stageUnit}>pedidos</span>
              </div>
              <div style={distribution.stagePct}>
                {`${pct.toFixed(0)}% · ${g.helper}`}
              </div>
            </div>
          );
        })}
      </div>

      <div style={distribution.bar} title="Proporção dos pedidos na linha do tempo">
        {GROUP_KEYS.map((key) => {
          const g = GROUPS[key];
          const v = counts[key] || 0;
          const width = totalNum > 0 ? (v / totalNum) * 100 : 0;
          return <div key={key} style={distribution.barSeg(g.color, width)} />;
        })}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Donut conic-gradient · 4 segmentos com hole branco no centro
// ─────────────────────────────────────────────────────────────────────────────
function DonutChart({ delivery }) {
  const segs = [
    { key: "sucesso", value: delivery.closed.sucesso, color: deliveryColors.sucesso },
    { key: "transito", value: delivery.open.transito, color: deliveryColors.transito },
    { key: "ocorrencia", value: delivery.open.ocorrencia, color: deliveryColors.ocorrencia },
    { key: "problema", value: delivery.closed.problema, color: deliveryColors.problema },
  ];
  const total = segs.reduce((s, x) => s + x.value, 0);

  let bg;
  if (total === 0) {
    bg = colors.border;
  } else {
    let acc = 0;
    const stops = [];
    for (const s of segs) {
      if (s.value <= 0) continue;
      const pct = (s.value / total) * 100;
      stops.push(`${s.color} ${acc}% ${acc + pct}%`);
      acc += pct;
    }
    bg = `conic-gradient(${stops.join(", ")})`;
  }

  return (
    <div style={donut.chartBox}>
      <div style={{ width: "100%", height: "100%", borderRadius: "50%", background: bg }} />
      <div style={donut.hole}>
        <div style={donut.holeValue}>{fmtInt(total)}</div>
        <div style={donut.holeLabel}>TOTAL</div>
      </div>
    </div>
  );
}

function DeliveryCard({ delivery }) {
  return (
    <article style={deliveryRow.card}>
      <div style={deliveryRow.accent(deliveryColors.sucesso)} />
      <div style={deliveryRow.head}>
        <h2 style={deliveryRow.title}>ENTREGAS</h2>
      </div>
      <div style={donut.wrap}>
        <DonutChart delivery={delivery} />
        <div style={donut.breakdownGrid}>
          <div style={donut.group}>
            <div style={donut.groupTitle}>EM ABERTO</div>
            <div style={donut.row}>
              <span style={donut.rowLabel}>
                <span style={donut.rowDot(deliveryColors.transito)} />
                Em Trânsito
              </span>
              <span style={donut.rowValue(deliveryColors.transito)}>
                {fmtInt(delivery.open.transito)}
              </span>
            </div>
            <div style={donut.row}>
              <span style={donut.rowLabel}>
                <span style={donut.rowDot(deliveryColors.ocorrencia)} />
                Com Ocorrência
              </span>
              <span style={donut.rowValue(deliveryColors.ocorrencia)}>
                {fmtInt(delivery.open.ocorrencia)}
              </span>
            </div>
          </div>
          <div style={donut.group}>
            <div style={donut.groupTitle}>CONCLUÍDAS</div>
            <div style={donut.row}>
              <span style={donut.rowLabel}>
                <span style={donut.rowDot(deliveryColors.problema)} />
                Com Problema
              </span>
              <span style={donut.rowValue(deliveryColors.problema)}>
                {fmtInt(delivery.closed.problema)}
              </span>
            </div>
            <div style={donut.row}>
              <span style={donut.rowLabel}>
                <span style={donut.rowDot(deliveryColors.sucesso)} />
                Com Sucesso
              </span>
              <span style={donut.rowValue(deliveryColors.sucesso)}>
                {fmtInt(delivery.closed.sucesso)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

function PerformanceCard({ performance }) {
  const pct = performance.pct;
  const good = pct == null ? null : pct >= 90;
  const emoji = pct == null ? "·" : pct >= 90 ? "😊" : pct >= 70 ? "😐" : "😞";

  return (
    <article style={deliveryRow.card}>
      <div style={deliveryRow.accent(deliveryColors.transito)} />
      <div style={deliveryRow.head}>
        <h2 style={deliveryRow.title}>PERFORMANCE DE ENTREGA</h2>
      </div>
      <div style={perfStyle.body}>
        <div style={perfStyle.emoji}>{emoji}</div>
        <div style={perfStyle.value(good)}>
          {pct == null ? "—" : `${fmtPct(pct)}%`}
        </div>
        <div style={perfStyle.counts}>
          <span style={perfStyle.countItem}>
            <span style={perfStyle.countNumber(deliveryColors.sucesso)}>
              {fmtInt(performance.onTime)}
            </span>
            no prazo
          </span>
          <span style={perfStyle.countItem}>
            <span style={perfStyle.countNumber(deliveryColors.problema)}>
              {fmtInt(performance.late)}
            </span>
            em atraso
          </span>
        </div>
      </div>
    </article>
  );
}

const CARRIER_LEGEND = [
  { label: "Com Sucesso", color: deliveryColors.sucesso },
  { label: "Em Trânsito", color: deliveryColors.transito },
  { label: "Com Ocorrência", color: deliveryColors.ocorrencia },
  { label: "Com Problema", color: deliveryColors.problema },
];

function CarriersCard({ carriersList }) {
  return (
    <article style={deliveryRow.card}>
      <div style={deliveryRow.accent(deliveryColors.sucesso)} />
      <div style={deliveryRow.head}>
        <h2 style={deliveryRow.title}>ENTREGAS POR TRANSPORTADORA</h2>
      </div>
      <div style={carriersStyle.legend}>
        {CARRIER_LEGEND.map((item) => (
          <span key={item.label} style={carriersStyle.legendItem}>
            <span style={carriersStyle.legendDot(item.color)} />
            {item.label}
          </span>
        ))}
      </div>
      <div style={carriersStyle.meta}>
        <span>TOTAL DE TRANSPORTADORAS: {carriersList.length}</span>
        <span>TOTAL</span>
      </div>
      <div style={carriersStyle.body}>
        {carriersList.length === 0 && (
          <div style={carriersStyle.empty}>Sem entregas no período</div>
        )}
        {carriersList.map((c) => {
          const segs = [
            { key: "sucesso", value: c.sucesso, color: deliveryColors.sucesso },
            { key: "transito", value: c.transito, color: deliveryColors.transito },
            { key: "ocorrencia", value: c.ocorrencia, color: deliveryColors.ocorrencia },
            { key: "problema", value: c.problema, color: deliveryColors.problema },
          ];
          return (
            <div key={c.name} style={carriersStyle.row}>
              <span style={carriersStyle.name} title={c.name}>
                {c.name}
              </span>
              <div style={carriersStyle.barWrap}>
                {segs.map((s) => {
                  const width = c.total > 0 ? (s.value / c.total) * 100 : 0;
                  if (width === 0) return null;
                  return (
                    <div
                      key={s.key}
                      style={carriersStyle.barSeg(s.color, width)}
                      title={`${s.key}: ${s.value}`}
                    />
                  );
                })}
              </div>
              <span style={carriersStyle.total}>{fmtInt(c.total)}</span>
            </div>
          );
        })}
      </div>
    </article>
  );
}

function DeliveryRow({ stats }) {
  return (
    <section style={deliveryRow.grid}>
      <DeliveryCard delivery={stats.delivery} />
      <PerformanceCard performance={stats.performance} />
      <CarriersCard carriersList={stats.carriersList} />
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Mapa geográfico do Brasil (choropleth) · nomes e config de cores por view
// ─────────────────────────────────────────────────────────────────────────────
const UF_NAMES = {
  AC: "Acre", AL: "Alagoas", AP: "Amapá", AM: "Amazonas", BA: "Bahia",
  CE: "Ceará", DF: "Distrito Federal", ES: "Espírito Santo", GO: "Goiás",
  MA: "Maranhão", MT: "Mato Grosso", MS: "Mato Grosso do Sul", MG: "Minas Gerais",
  PA: "Pará", PB: "Paraíba", PR: "Paraná", PE: "Pernambuco", PI: "Piauí",
  RJ: "Rio de Janeiro", RN: "Rio Grande do Norte", RS: "Rio Grande do Sul",
  RO: "Rondônia", RR: "Roraima", SC: "Santa Catarina", SP: "São Paulo",
  SE: "Sergipe", TO: "Tocantins",
};

const BR_VIEWS = {
  entregas: { label: "ENTREGAS", light: "#E4F0E8", dark: "#176B3E", color: "#176B3E" },
  atrasos: { label: "ATRASOS", light: "#FBE5E2", dark: "#9E2B1E", color: "#9E2B1E" },
};

// viewBox original do mapa é "0 0 613 639". Ampliamos à direita para abrir
// espaço aos rótulos dos estados pequenos do Nordeste (callouts).
const MAP_VIEWBOX = "0 0 770 639";

// Estados pequenos/agrupados: rótulo reposicionado em área livre + linha guia.
// Coordenadas no sistema do viewBox ampliado.
const BR_LABEL_OVERRIDES = {
  CE: [705, 100],
  RN: [712, 145],
  PB: [712, 190],
  PE: [712, 235],
  AL: [712, 280],
  SE: [712, 325],
  DF: [455, 296],
};

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

// Interpola entre duas cores hex; t ∈ [0,1]
function mixHex(from, to, t) {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  const ch = (i) => Math.round(a[i] + (b[i] - a[i]) * t);
  return `rgb(${ch(0)}, ${ch(1)}, ${ch(2)})`;
}

function BrazilMapPanel({ stats, onSelect }) {
  const [view, setView] = useState("entregas");
  const cfg = BR_VIEWS[view];

  // Combina entregas + atrasos por UF. pct = atrasos / entregas
  const ufStats = {};
  for (const e of stats.deliveriesList) {
    ufStats[e.uf] = { ent: e.count, entOrders: e.orders, atr: 0, atrOrders: [], pct: 0 };
  }
  for (const e of stats.lateList) {
    const s = ufStats[e.uf] || { ent: 0, entOrders: [], atr: 0, atrOrders: [], pct: 0 };
    s.atr = e.count;
    s.atrOrders = e.orders;
    ufStats[e.uf] = s;
  }
  for (const uf in ufStats) {
    const s = ufStats[uf];
    s.pct = s.ent > 0 ? (s.atr / s.ent) * 100 : 0;
  }

  const isAtraso = view === "atrasos";
  // Métrica que define a cor: entregas → contagem; atrasos → taxa de atraso (%)
  const colorValueOf = (s) => (isAtraso ? s.pct : s.ent);
  const colorMax = Object.values(ufStats).reduce(
    (m, s) => (s.ent > 0 && colorValueOf(s) > m ? colorValueOf(s) : m),
    0
  );
  const totalView = Object.values(ufStats).reduce(
    (sum, s) => sum + (isAtraso ? s.atr : s.ent),
    0
  );

  return (
    <section style={brmap.wrap}>
      <div style={brmap.head}>
        <h2 style={brmap.title}>MAPA · {cfg.label} POR ESTADO</h2>
        <div style={brmap.toggle}>
          {Object.entries(BR_VIEWS).map(([key, v]) => (
            <button
              key={key}
              type="button"
              onClick={() => setView(key)}
              style={brmap.toggleBtn(view === key, v.color)}
            >
              {v.label}
            </button>
          ))}
        </div>
        <div style={brmap.totalBlock}>
          <span style={brmap.totalLabel}>TOTAL</span>
          <span style={brmap.totalValue}>{fmtInt(totalView)}</span>
        </div>
      </div>

      <div style={brmap.body}>
        <div style={brmap.mapBox}>
          <svg
            viewBox={MAP_VIEWBOX}
            style={brmap.svg}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label={`Mapa do Brasil · ${cfg.label.toLowerCase()} por estado`}
          >
            {/* Contornos pintados pela view atual:
                entregas → intensidade por volume (sqrt) ·
                atrasos  → intensidade pela taxa de atraso (% linear, maior = mais escuro) */}
            {Object.entries(BR_PATHS).map(([uf, d]) => {
              const s = ufStats[uf];
              const hasData = !!s && s.ent > 0;
              const cv = hasData ? colorValueOf(s) : 0;
              const t =
                colorMax > 0 && hasData
                  ? isAtraso
                    ? cv / colorMax
                    : Math.sqrt(cv / colorMax)
                  : 0;
              const bg = hasData ? mixHex(cfg.light, cfg.dark, t) : colors.bg;
              const ent = s?.ent || 0;
              const atr = s?.atr || 0;
              const pct = Math.round(s?.pct || 0);
              const clickable = isAtraso ? atr > 0 : ent > 0;
              const orders = isAtraso ? s?.atrOrders : s?.entOrders;
              const tooltip = `${UF_NAMES[uf] || uf} · ${fmtInt(ent)} entregas · ${fmtInt(atr)} atrasos (${pct}%)`;
              return (
                <path
                  key={uf}
                  d={d}
                  className={clickable ? "tracking-uf-path" : undefined}
                  style={brmap.path(bg, clickable)}
                  onClick={
                    clickable
                      ? () =>
                          onSelect({
                            title: `${cfg.label} · ${uf}`,
                            orders: orders || [],
                          })
                      : undefined
                  }
                >
                  <title>{tooltip}</title>
                </path>
              );
            })}

            {/* Rótulos: entregas, atrasos e % — persistem em ambas as views.
                Estados pequenos têm o rótulo deslocado e ligado por linha guia. */}
            {Object.entries(BR_CENTROIDS).map(([uf, [cx, cy]]) => {
              const s = ufStats[uf];
              const ent = s?.ent || 0;
              if (ent === 0) return null;
              const atr = s?.atr || 0;
              const pct = Math.round(s?.pct || 0);
              const override = BR_LABEL_OVERRIDES[uf];
              const lx = override ? override[0] : cx;
              const ly = override ? override[1] : cy;
              return (
                <g key={uf}>
                  {override && (
                    <>
                      <line x1={cx} y1={cy} x2={lx} y2={ly} style={brmap.leader} />
                      <circle cx={cx} cy={cy} r={2.4} style={brmap.leaderDot} />
                    </>
                  )}
                  <text x={lx} y={ly} style={brmap.label}>
                    <tspan x={lx} dy="-0.75em" style={brmap.labelUf}>{uf}</tspan>
                    {isAtraso ? (
                      <>
                        <tspan x={lx} dy="1.15em" style={brmap.labelAtr}>{fmtInt(atr)} · {pct}%</tspan>
                        <tspan x={lx} dy="1.15em" style={brmap.labelEnt}>{fmtInt(ent)}</tspan>
                      </>
                    ) : (
                      <>
                        <tspan x={lx} dy="1.15em" style={brmap.labelEnt}>{fmtInt(ent)}</tspan>
                        <tspan x={lx} dy="1.15em" style={brmap.labelAtr}>{fmtInt(atr)} · {pct}%</tspan>
                      </>
                    )}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        <div style={brmap.legend}>
          <span style={brmap.legendLabel}>{isAtraso ? "0%" : "0"}</span>
          <div style={brmap.legendBar(cfg.light, cfg.dark)} />
          <span style={brmap.legendLabel}>
            {isAtraso
              ? `${Math.round(colorMax)}% atraso`
              : `${fmtInt(colorMax)} entregas`}
          </span>
          <span style={brmap.legendSep} />
          <span style={brmap.legendItem}>
            <span style={brmap.legendDot("#176B3E")} /> entregas
          </span>
          <span style={brmap.legendItem}>
            <span style={brmap.legendDot("#9E2B1E")} /> atrasos · %
          </span>
          <span style={brmap.legendSep} />
          <span style={brmap.legendItem}>
            <span style={brmap.legendDot(colors.bg)} /> sem dados
          </span>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal de pedidos por UF
// ─────────────────────────────────────────────────────────────────────────────
function statusBadgeVariant(statusStr) {
  const s = norm(statusStr);
  if (!s) return "neutral";
  if (s.includes("ATRAS")) return "late";
  if (s.includes("ANTECIP") || s.includes("ENTREGUE NO DIA") || s.includes("DENTRO DO PRAZO"))
    return "success";
  if (s.includes("ANDAMENTO") || s.includes("ENVIADO")) return "transit";
  if (s.includes("AGUARDANDO") || s.includes("PENDENTE")) return "warning";
  return "neutral";
}

function fmtDateBR(iso) {
  const d = parseISO(iso);
  if (!d) return "—";
  return d.toLocaleDateString("pt-BR");
}

// Data COM hora. O último status do rastreio muda várias vezes no mesmo dia
// ("saiu para entrega" de manhã, "entregue" à tarde), então só a data não diz
// se a informação é recente.
//
// Parse LITERAL, sem new Date(): os timestamps do ERP chegam com "Z" mas o
// valor já é horário local. Passando pelo new Date(), um status das 14:22
// apareceria como 11:22 — três horas atrás, bem no campo que existe pra
// dizer se o dado é recente.
function fmtDateTimeBR(value) {
  if (!value) return "—";
  const m = String(value).match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/
  );
  if (!m) return "—";
  const [, year, month, day, hour, minute] = m;
  if (Number(year) < 2000) return "—"; // sentinela de data vazia do ERP
  return `${day}/${month}/${year} ${hour}:${minute}`;
}

// 12345678 | "12345-678" | 1310100 → "12345-678".
//
// O padStart é o que importa: metade dos CEPs de São Paulo começa com zero,
// e se a API mandar o campo como NÚMERO o zero já vem perdido.
function fmtCep(v) {
  const digits = String(v ?? "").replace(/\D/g, "");
  if (!digits || digits.length > 8) return "—";
  const cep = digits.padStart(8, "0");
  return `${cep.slice(0, 5)}-${cep.slice(5)}`;
}

function parseFreight(v) {
  const n = Number(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

// Limita exibição a `max` caracteres (mantém o valor completo no tooltip)
function truncate(str, max) {
  const s = String(str ?? "");
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

// Exporta a listagem atual do modal para XLSX (SheetJS)
function exportOrdersToXlsx(title, orders) {
  if (!orders || orders.length === 0) return;
  const rows = orders.map((o) => ({
    "Pedido": o.Order || o.IDOrder || "",
    "Status Pedido": o.StatusOrder || "",
    "Cliente": o.ConsumerNameCorporateName || "",
    "Cidade": o.ShippingCity || "",
    "UF": o.ShippingState || "",
    // CEP como texto: em número, o Excel come o zero à esquerda dos CEPs
    // que começam com 0 — metade de São Paulo.
    "CEP": fmtCep(o.ShippingPostalCode),
    "Transportadora": o.SupplierNameCorporateName || "",
    "Cliente SLA": o.StatusDeliveryConsumer || "",
    "Transp. SLA": o.StatusDeliveryCarrier || "",
    "Último Status Rastreio": o.TrackingLastStatus || "",
    "Data Último Status": fmtDateTimeBR(o.TrackingLastStatusRecordTimestamp),
    "Data de Envio": fmtDateBR(o.ShippingDate),
    "Data Est. Entrega": fmtDateBR(o.ShippingEstimateDate),
    "Data Transportadora": fmtDateBR(o.CarrierDeliveryDate),
    "Frete": parseFreight(o.ValueShipping) ?? "",
    "Nº Rastreio": o.ShippingId || "",
  }));

  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  const slug = (title || "pedidos")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();
  const sheetName = (title || "Pedidos").replace(/[\\/?*[\]:]/g, "").slice(0, 31);
  XLSX.utils.book_append_sheet(wb, ws, sheetName || "Pedidos");
  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `tracking_${slug || "pedidos"}_${stamp}.xlsx`);
}

function OrdersModal({ selection, onClose }) {
  if (!selection) return null;
  const { title, orders } = selection;
  const subtitle = `${orders.length} pedido${orders.length === 1 ? "" : "s"}`;

  return (
    <div style={modalStyle.overlay} onClick={onClose}>
      <div style={modalStyle.box} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyle.head}>
          <div style={modalStyle.headLeft}>
            <h3 style={modalStyle.title}>{title}</h3>
            <span style={modalStyle.subtitle}>{subtitle}</span>
          </div>
          <div style={modalStyle.headActions}>
            <button
              type="button"
              className="tracking-export-btn"
              style={modalStyle.exportBtn}
              onClick={() => exportOrdersToXlsx(title, orders)}
              disabled={orders.length === 0}
            >
              ⬇ Exportar XLSX
            </button>
            <button
              type="button"
              className="tracking-modal-close"
              style={modalStyle.close}
              onClick={onClose}
              aria-label="Fechar"
            >
              ×
            </button>
          </div>
        </div>
        <div style={modalStyle.body}>
          {orders.length === 0 ? (
            <div style={modalStyle.empty}>Sem pedidos para listar.</div>
          ) : (
            <table style={modalStyle.table}>
              <thead>
                <tr>
                  <th style={modalStyle.th}>Pedido</th>
                  <th style={modalStyle.th}>Status Pedido</th>
                  <th style={modalStyle.th}>CEP</th>
                  <th style={modalStyle.th}>Transportadora</th>
                  <th style={modalStyle.th}>Cliente SLA</th>
                  <th style={modalStyle.th}>Transp. SLA</th>
                  <th style={modalStyle.th}>Último Status Rastreio</th>
                  <th style={modalStyle.th}>Data Último Status</th>
                  <th style={modalStyle.th}>Data de Envio</th>
                  <th style={modalStyle.th}>Data de Entrega</th>
                  <th style={modalStyle.th}>Data Transportadora</th>
                  <th style={modalStyle.th}>Frete</th>
                  <th style={modalStyle.th}>Nº Rastreio</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  const freight = parseFreight(o.ValueShipping);
                  return (
                    <tr key={o.IDOrder ?? o.Order}>
                      <td style={{ ...modalStyle.td, ...modalStyle.tdMono }}>
                        {o.Order || o.IDOrder || "—"}
                      </td>
                      <td style={modalStyle.td}>{o.StatusOrder || "—"}</td>
                      <td style={{ ...modalStyle.td, ...modalStyle.tdMono }}>
                        {fmtCep(o.ShippingPostalCode)}
                      </td>
                      <td style={modalStyle.td}>
                        {o.SupplierNameCorporateName || "—"}
                      </td>
                      <td style={modalStyle.td}>
                        <span
                          style={modalStyle.badge(
                            statusBadgeVariant(o.StatusDeliveryConsumer)
                          )}
                        >
                          {o.StatusDeliveryConsumer || "—"}
                        </span>
                      </td>
                      <td style={modalStyle.td}>
                        <span
                          style={modalStyle.badge(
                            statusBadgeVariant(o.StatusDeliveryCarrier)
                          )}
                        >
                          {o.StatusDeliveryCarrier || "—"}
                        </span>
                      </td>
                      {/* Status livre da transportadora: pode vir longo
                          ("Objeto saiu para entrega ao destinatário"), então
                          corta e mantém o texto inteiro no tooltip. */}
                      <td
                        style={modalStyle.td}
                        title={o.TrackingLastStatus || ""}
                      >
                        {o.TrackingLastStatus ? truncate(o.TrackingLastStatus, 34) : "—"}
                      </td>
                      <td style={{ ...modalStyle.td, ...modalStyle.tdMono }}>
                        {fmtDateTimeBR(o.TrackingLastStatusRecordTimestamp)}
                      </td>
                      <td style={{ ...modalStyle.td, ...modalStyle.tdMono }}>
                        {fmtDateBR(o.ShippingDate)}
                      </td>
                      <td style={{ ...modalStyle.td, ...modalStyle.tdMono }}>
                        {fmtDateBR(o.ShippingEstimateDate)}
                      </td>
                      <td style={{ ...modalStyle.td, ...modalStyle.tdMono }}>
                        {fmtDateBR(o.CarrierDeliveryDate)}
                      </td>
                      <td style={{ ...modalStyle.td, ...modalStyle.tdMono }}>
                        {freight != null
                          ? `R$ ${freight.toLocaleString("pt-BR", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}`
                          : "—"}
                      </td>
                      <td
                        style={{ ...modalStyle.td, ...modalStyle.tdMono }}
                        title={o.ShippingId || ""}
                      >
                        {o.ShippingId ? truncate(o.ShippingId, 28) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function FooterBar() {
  return (
    <footer style={footer.wrap}>
      <div style={footer.legend}>
        {GROUP_KEYS.map((key) => (
          <div key={key} style={footer.legendItem}>
            <span style={footer.swatch(GROUPS[key].color)} /> {GROUPS[key].label}
          </div>
        ))}
      </div>
      <div style={footer.schedule}>
        Fonte: /orders/tracking · canais Vtex + idworks · janela: 30 dias (14 antes · hoje · 15 depois) · entregues = hoje
      </div>
    </footer>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Estado inicial vazio (mostra "—" enquanto carrega a primeira vez)
// ─────────────────────────────────────────────────────────────────────────────
const EMPTY_STATS = {
  total: 0,
  counts: {
    andamento: 0,
    risco: 0,
    atrasados: 0,
    antecipado: 0,
    noPrazo: 0,
    entregueAtraso: 0,
  },
  countsFull: {
    andamento: 0,
    risco: 0,
    atrasados: 0,
    antecipado: 0,
    noPrazo: 0,
    entregueAtraso: 0,
  },
  timelineOrders: {
    andamento: [],
    risco: [],
    atrasados: [],
    antecipado: [],
    noPrazo: [],
    entregueAtraso: [],
  },
  timelineOrdersFull: {
    andamento: [],
    risco: [],
    atrasados: [],
    antecipado: [],
    noPrazo: [],
    entregueAtraso: [],
  },
  deliveriesList: [],
  lateList: [],
  delivery: {
    total: 0,
    open: { transito: 0, ocorrencia: 0 },
    closed: { sucesso: 0, problema: 0 },
  },
  performance: { pct: null, onTime: 0, late: 0 },
  carriersList: [],
};

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────
function TrackingPerformance(props) {
  const { location } = props;
  const idPage = location?.idPage;

  const [auth, setAuth] = useState(null);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [modalSelection, setModalSelection] = useState(null);

  // Período. `draftRange` é o que está nos inputs; `appliedRange` é o que a
  // busca usa — separados pra digitar a data não disparar requisição a cada
  // tecla. null = janela padrão do dashboard.
  const [draftRange, setDraftRange] = useState(defaultRange);
  const [appliedRange, setAppliedRange] = useState(null);

  const applyRange = () => {
    if (!draftRange.from || !draftRange.to) return;
    // Invertido é erro de digitação, não intenção: desinverte em vez de
    // devolver uma tela vazia sem explicar por quê.
    const ordered =
      localInputToMs(draftRange.from) > localInputToMs(draftRange.to)
        ? { from: draftRange.to, to: draftRange.from }
        : draftRange;
    setDraftRange(ordered);
    setAppliedRange(ordered);
  };

  const clearRange = () => {
    setDraftRange(defaultRange());
    setAppliedRange(null);
  };

  // Autenticação por idPage (padrão do projeto)
  useEffect(() => {
    async function verifyAuth() {
      try {
        const { profile } = store.getState().user;
        const res = await apiLabs.post("valid_key", {
          id: profile.id,
          menu_id: idPage,
        });
        if (res.data.message === "User not authorized") return setAuth("denied");
        if (res.data.message === "User authorized") return setAuth("authorized");
        history.push({ pathname: "/", state: { from: location } });
      } catch {
        setAuth("denied");
      }
    }
    if (idPage) verifyAuth();
    else setAuth("authorized");
  }, [idPage, location]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Sem filtro: 30 dias com hoje no meio (14 atrás + hoje + 15 à frente).
      // Com filtro: o período escolhido.
      const range = appliedRange || defaultRange();
      const baseParams = {
        // A API aceita data, não data+hora — a busca vem em dias inteiros e o
        // recorte por hora é aplicado no resultado, logo abaixo.
        ShippingEstimateDateFrom: datePartOf(range.from),
        ShippingEstimateDateTo: datePartOf(range.to),
      };

      const results = await Promise.all(
        SALES_CHANNELS.map((channel) =>
          fetchAllPages("/orders/tracking", { ...baseParams, ...channel })
        )
      );

      // Dedup cross-canal por IDOrder (defensivo — caso o backend ignore SalesChannelName)
      const ordersById = new Map();
      for (const list of results) {
        for (const o of list) {
          const key = o?.IDOrder != null ? o.IDOrder : `${o?.Order}#${ordersById.size}`;
          if (!ordersById.has(key)) ordersById.set(key, o);
        }
      }
      const orders = [...ordersById.values()];
      const inRange = filterByEstimateRange(orders, range);

      console.log(
        `[Tracking] params { ShippingEstimateDateFrom: ${baseParams.ShippingEstimateDateFrom}, ShippingEstimateDateTo: ${baseParams.ShippingEstimateDateTo} } · ${SALES_CHANNELS.map((c, i) => `${c.SalesChannelName}=${results[i].length}`).join(" · ")} · total=${orders.length}` +
        (orders.length !== inRange.length ? ` · recorte por hora: ${inRange.length}` : "")
      );

      setStats(buildStats(inRange));
      setLastUpdate(new Date());
    } catch (err) {
      console.error("Tracking — erro ao carregar dados:", err);
      setError("Erro ao carregar dados. Verifique a conexão e tente novamente.");
    } finally {
      setLoading(false);
    }
  }, [appliedRange]);

  useEffect(() => {
    if (auth !== "authorized") return;
    fetchData();
    // O auto-refresh respeita o filtro: fetchData muda quando appliedRange
    // muda, e o efeito reinicia com a janela nova.
    const interval = setInterval(fetchData, REFRESH_MS);
    return () => clearInterval(interval);
  }, [auth, fetchData]);

  useEffect(() => {
    if (!modalSelection) return;
    function onKey(e) {
      if (e.key === "Escape") setModalSelection(null);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalSelection]);

  if (auth === "denied") return <Denied />;
  if (auth === null) return null;

  const statusLabel = loading
    ? "ATUALIZANDO..."
    : lastUpdate
    ? `ATUALIZADO ${format(lastUpdate, "HH:mm")}`
    : "AGUARDANDO DADOS";

  return (
    <>
      <style>{globalCSS}</style>

      <div style={page}>
        <div style={dash}>
          <header style={header.wrap}>
            <div>
              <h1 style={header.title}>TRACKING · PERFORMANCE</h1>
              <p style={header.subtitle}>
                Lojasantoantonio · Visão dual cliente × transportadora · Vtex
              </p>
            </div>
            <div style={header.right}>
              <div
                onClick={loading ? undefined : fetchData}
                style={header.pill(loading)}
                title={loading ? "Atualizando..." : "Clique para atualizar agora"}
              >
                <span style={header.pillDot(loading)} />
                <span>{statusLabel}</span>
              </div>
              <Clock />
            </div>
          </header>

          <div style={filterBar.wrap(!!appliedRange)}>
            <div style={filterBar.field}>
              <label style={filterBar.label} htmlFor="tracking-de">Início</label>
              <input
                id="tracking-de"
                type="datetime-local"
                style={filterBar.input}
                value={draftRange.from}
                onChange={(e) => setDraftRange((r) => ({ ...r, from: e.target.value }))}
              />
            </div>
            <div style={filterBar.field}>
              <label style={filterBar.label} htmlFor="tracking-ate">Fim</label>
              <input
                id="tracking-ate"
                type="datetime-local"
                style={filterBar.input}
                value={draftRange.to}
                onChange={(e) => setDraftRange((r) => ({ ...r, to: e.target.value }))}
              />
            </div>
            <div style={filterBar.actions}>
              <button
                type="button"
                style={filterBar.btn(true)}
                onClick={applyRange}
                disabled={loading}
              >
                Aplicar
              </button>
              <button
                type="button"
                style={filterBar.btn(false)}
                onClick={clearRange}
                disabled={loading || !appliedRange}
              >
                Limpar filtro
              </button>
            </div>
            <span style={filterBar.note(!!appliedRange)}>
              {appliedRange
                ? `Período filtrado por data estimada de entrega: ${fmtDateTimeBR(appliedRange.from)} até ${fmtDateTimeBR(appliedRange.to)}.`
                : `Janela padrão: ${WINDOW_DAYS_BACK} dias atrás, hoje e ${WINDOW_DAYS_AHEAD} dias à frente, por data estimada de entrega.`}
            </span>
          </div>

          {error && (
            <div
              style={{
                background: colors.badSoft,
                border: `1px solid ${colors.bad}`,
                borderRadius: 8,
                padding: "12px 18px",
                color: colors.bad,
                fontSize: 14,
                fontWeight: 600,
                letterSpacing: "0.02em",
              }}
            >
              ⚠ {error}
            </div>
          )}

          <DistributionPanel
            title="LINHA DO TEMPO DOS PEDIDOS"
            total={stats.total}
            counts={stats.counts}
            timelineOrders={stats.timelineOrders}
            onSelect={setModalSelection}
          />
          <DeliveryRow stats={stats} />
          <BrazilMapPanel stats={stats} onSelect={setModalSelection} />
          <DistributionPanel
            title="TOTAL DO PERÍODO · ÚLTIMOS 15 DIAS"
            total={stats.total}
            counts={stats.countsFull}
            timelineOrders={stats.timelineOrdersFull}
            onSelect={setModalSelection}
          />
          <FooterBar />
        </div>
      </div>

      <OrdersModal
        selection={modalSelection}
        onClose={() => setModalSelection(null)}
      />
    </>
  );
}

export default TrackingPerformance;
